import { RecentRow } from './RecentRow'
import { SearchResult } from './SearchResult'
import { ErrorBanner } from './ErrorBanner'
import type { Recent } from './recents'
import type { Result } from './mapResults'
import type { SearchState } from './useSearch'
import styles from './SearchDropdown.module.css'

/**
 * SearchDropdown — renders the six dropdown branches (UI-SPEC §Components 2
 * content modes 1-7, lines 251-258):
 *
 *   - `tooShort`   → "Keep typing…" hint row
 *   - `loading`    → previous content reuse; we render a hint if nothing else
 *   - `results`    → list of <SearchResult/>
 *   - `empty`      → "No matches. Try a different search or check spelling."
 *   - `error`      → <ErrorBanner/> (role="alert")
 *   - `idle` + recents.length > 0 → "Recent" header + <RecentRow/> list
 *   - `idle` + no recents → search-hint copy
 *
 * `role="listbox"` per UI-SPEC §ARIA roles summary.
 * Kept intentionally pure — no localStorage reads/writes here; parent owns
 * recents state so the list can refresh after writeRecent on select.
 */
export interface SearchDropdownProps {
  state: SearchState
  recents: Recent[]
  onSelectResult: (r: Result) => void
  onSelectRecent: (r: Recent) => void
  onClearRecents: () => void
  listboxId?: string
}

export function SearchDropdown({
  state,
  recents,
  onSelectResult,
  onSelectRecent,
  onClearRecents,
  listboxId = 'search-dropdown',
}: SearchDropdownProps) {
  return (
    <div role="listbox" id={listboxId} className={styles.panel}>
      {state.status === 'tooShort' && (
        <div className={styles.hint}>Keep typing…</div>
      )}

      {state.status === 'loading' && (
        <div className={styles.hint}>Searching…</div>
      )}

      {state.status === 'results' && state.results.length > 0 && (
        <>
          {state.results.map((r, idx) => (
            <SearchResult
              key={r.id}
              id={r.id}
              label={r.label}
              sublabel={r.sublabel}
              optionId={`search-option-${idx}`}
              onSelect={() => onSelectResult(r)}
            />
          ))}
        </>
      )}

      {state.status === 'empty' && (
        <div className={styles.emptyState}>
          No matches. Try a different search or check spelling.
        </div>
      )}

      {state.status === 'error' && <ErrorBanner kind={state.kind} />}

      {state.status === 'idle' && recents.length > 0 && (
        <>
          <div className={styles.recentsHeader} role="presentation">
            <span>Recent</span>
            <button
              type="button"
              className={styles.clearButton}
              aria-label="Clear recent searches"
              onClick={onClearRecents}
            >
              Clear
            </button>
          </div>
          {recents.map((r, idx) => (
            <RecentRow
              key={r.id}
              id={r.id}
              label={r.label}
              sublabel={r.sublabel}
              savedAt={r.savedAt}
              optionId={`search-option-${idx}`}
              onSelect={() => onSelectRecent(r)}
            />
          ))}
        </>
      )}

      {state.status === 'idle' && recents.length === 0 && (
        <div className={styles.hint}>Search places, addresses, or POIs</div>
      )}
    </div>
  )
}
